import { QueryClient } from '@tanstack/react-query';

/**
 * One cached query layer over the Firestore SDK.
 *
 * Realtime subscriptions are reserved for the three places that earn them: the
 * running timer, the notification unread count, and the open task detail. Every
 * other read is a cached query, because an onSnapshot per list is what turns a
 * 2 USD per seat product into a loss.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Firestore data is not volatile at this scale. A minute of staleness
      // costs nothing and saves a great many reads.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Permission errors never succeed on retry, and retrying them just
        // burns quota and delays the message the user needs to see.
        const code = (error as { code?: string })?.code;
        if (code === 'permission-denied' || code === 'not-found') return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/**
 * Query keys, in one place.
 *
 * Every key starts with the org id, so switching orgs cannot show a cached
 * page belonging to the previous one.
 */
export const keys = {
  org: (orgId: string) => ['org', orgId] as const,
  members: (orgId: string) => ['org', orgId, 'members'] as const,
  clients: (orgId: string) => ['org', orgId, 'clients'] as const,
  projects: (orgId: string) => ['org', orgId, 'projects'] as const,
  project: (orgId: string, projectId: string) => ['org', orgId, 'projects', projectId] as const,
  tasks: (orgId: string, filters: unknown) => ['org', orgId, 'tasks', filters] as const,
  task: (orgId: string, taskId: string) => ['org', orgId, 'tasks', taskId] as const,
  timeEntries: (orgId: string, filters: unknown) => ['org', orgId, 'timeEntries', filters] as const,
  timer: (orgId: string, uid: string) => ['org', orgId, 'timer', uid] as const,
  leaveRequests: (orgId: string, filters: unknown) =>
    ['org', orgId, 'leaveRequests', filters] as const,
  leaveBalances: (orgId: string, uid: string, year: number) =>
    ['org', orgId, 'leaveBalances', uid, year] as const,
  report: (orgId: string, report: string, params: unknown) =>
    ['org', orgId, 'report', report, params] as const,
  notifications: (orgId: string, uid: string) => ['org', orgId, 'notifications', uid] as const,
};
